import type { ChatLabels, WidgetLanguage } from './types';

/**
 * Label sets for the languages the application supports.
 *
 * These are keyed by the same codes used by `src/store/languageStore.js`, so the
 * widget can pick a set straight from the language toggle without a mapping
 * layer. Portuguese is the application default and the fallback.
 */

const pt: ChatLabels = {
  launcherOpen: 'Abrir chat',
  launcherClose: 'Fechar chat',
  title: 'Assistente Protheus',
  subtitle: 'Tire suas dúvidas sobre o Release',
  inputPlaceholder: 'Digite sua mensagem...',
  send: 'Enviar mensagem',
  stop: 'Parar resposta',
  attach: 'Anexar arquivos',
  removeAttachment: 'Remover anexo',
  newChat: 'Nova conversa',
  maximize: 'Maximizar',
  restore: 'Restaurar tamanho',
  close: 'Fechar',
  resize: 'Redimensionar a janela do chat',
  thumbsUp: 'Resposta útil',
  thumbsDown: 'Resposta não útil',
  feedbackThanks: 'Obrigado pelo seu feedback',
  feedbackPrompt: 'O que saiu errado?',
  feedbackPlaceholder: 'Opcional: conte-nos mais',
  feedbackSubmit: 'Enviar',
  feedbackSkip: 'Ignorar',
  copy: 'Copiar',
  copied: 'Copiado',
  typing: 'O assistente está digitando',
  errorGeneric: 'Algo deu errado. Por favor, tente novamente.',
  fileTooLarge: 'O arquivo é muito grande',
  tooManyFiles: 'Muitos arquivos',
  fileTypeRejected: 'Tipo de arquivo não permitido',
  attachmentsLabel: 'Anexos',
  assistantName: 'Assistente',
  youName: 'Você',
};

const en: ChatLabels = {
  launcherOpen: 'Open chat',
  launcherClose: 'Close chat',
  title: 'Protheus Assistant',
  subtitle: 'Ask anything about the Release',
  inputPlaceholder: 'Type your message...',
  send: 'Send message',
  stop: 'Stop response',
  attach: 'Attach files',
  removeAttachment: 'Remove attachment',
  newChat: 'New conversation',
  maximize: 'Maximize',
  restore: 'Restore size',
  close: 'Close',
  resize: 'Resize chat window',
  thumbsUp: 'Helpful',
  thumbsDown: 'Not helpful',
  feedbackThanks: 'Thanks for your feedback',
  feedbackPrompt: 'What went wrong?',
  feedbackPlaceholder: 'Optional: tell us more',
  feedbackSubmit: 'Submit',
  feedbackSkip: 'Skip',
  copy: 'Copy',
  copied: 'Copied',
  typing: 'Assistant is typing',
  errorGeneric: 'Something went wrong. Please try again.',
  fileTooLarge: 'File is too large',
  tooManyFiles: 'Too many files',
  fileTypeRejected: 'File type not allowed',
  attachmentsLabel: 'Attachments',
  assistantName: 'Assistant',
  youName: 'You',
};

const es: ChatLabels = {
  launcherOpen: 'Abrir chat',
  launcherClose: 'Cerrar chat',
  title: 'Asistente Protheus',
  subtitle: 'Consulta lo que quieras sobre el Release',
  inputPlaceholder: 'Escribe tu mensaje...',
  send: 'Enviar mensaje',
  stop: 'Detener respuesta',
  attach: 'Adjuntar archivos',
  removeAttachment: 'Quitar adjunto',
  newChat: 'Nueva conversación',
  maximize: 'Maximizar',
  restore: 'Restaurar tamaño',
  close: 'Cerrar',
  resize: 'Redimensionar la ventana del chat',
  thumbsUp: 'Respuesta útil',
  thumbsDown: 'Respuesta no útil',
  feedbackThanks: 'Gracias por tu comentario',
  feedbackPrompt: '¿Qué salió mal?',
  feedbackPlaceholder: 'Opcional: cuéntanos más',
  feedbackSubmit: 'Enviar',
  feedbackSkip: 'Omitir',
  copy: 'Copiar',
  copied: 'Copiado',
  typing: 'El asistente está escribiendo',
  errorGeneric: 'Algo salió mal. Inténtalo de nuevo.',
  fileTooLarge: 'El archivo es demasiado grande',
  tooManyFiles: 'Demasiados archivos',
  fileTypeRejected: 'Tipo de archivo no permitido',
  attachmentsLabel: 'Adjuntos',
  assistantName: 'Asistente',
  youName: 'Tú',
};

export const labelsByLanguage: Record<WidgetLanguage, ChatLabels> = { pt, en, es };

/** The application default language. */
export const DEFAULT_LANGUAGE: WidgetLanguage = 'pt';

/** Portuguese labels, used whenever a language is unknown. */
export const defaultLabels: ChatLabels = pt;

export function labelsFor(language?: string): ChatLabels {
  if (language && language in labelsByLanguage) {
    return labelsByLanguage[language as WidgetLanguage];
  }
  return defaultLabels;
}

export function mergeLabels(
  language?: string,
  overrides?: Partial<ChatLabels>,
): ChatLabels {
  const base = labelsFor(language);
  return overrides ? { ...base, ...overrides } : base;
}
